import { z } from 'zod';
/**
 * Kundenmails mit manueller Freigabe.
 *
 * Am Anfang will der Betreiber sehen, was beim Kunden ankommt, bevor es rausgeht. Alle Mails an
 * den Kunden **außer der Eingangsbestätigung nach dem Chat** laufen deshalb über eine
 * Warteschlange: Die Mail wird fertig gerendert in der Tabelle abgelegt und im Admin-Portal per
 * Knopfdruck freigegeben oder verworfen. Der Schalter `customerMailMode` stellt auf automatischen
 * Versand zurück, sobald das Vertrauen da ist.
 *
 * Eine wartende Mail trägt **keinen Magic-Link**: Der Link wird erst bei der Freigabe erzeugt,
 * damit nirgends ein gültiger Zugang zu einer Anfrage liegt, der nie verschickt wurde.
 */
/** Die sechs Kundenmails, die manuell freigegeben werden können. */
export const CustomerMailKindSchema = z.enum([
    'broadcast_notice',
    'first_offer',
    'offers_closed',
    'no_offers',
    'award_received',
    'award_confirmation',
]);
export const CUSTOMER_MAIL_KIND_LABELS = {
    broadcast_notice: 'Ausschreibung gestartet',
    first_offer: 'Erstes Angebot eingegangen',
    offers_closed: 'Angebote liegen vor',
    no_offers: 'Kein Angebot eingegangen',
    award_received: 'Auftrag angekommen',
    award_confirmation: 'Zuschlag mit Kontaktdaten',
};
/**
 * `sending` ist der Zwischenzustand während des Versands — er verhindert, dass zwei Klicks
 * dieselbe Mail zweimal verschicken. Scheitert der Versand, geht die Mail zurück auf `pending`.
 */
export const CustomerMailStatusSchema = z.enum(['pending', 'sending', 'sent', 'discarded']);
export const CUSTOMER_MAIL_STATUS_LABELS = {
    pending: 'Wartet auf Freigabe',
    sending: 'Wird gesendet',
    sent: 'Gesendet',
    discarded: 'Verworfen',
};
/** `manual`: Kundenmails warten auf Freigabe. `auto`: sie gehen sofort raus. */
export const CustomerMailModeSchema = z.enum(['manual', 'auto']);
/** Eine Zeile der Liste — ohne HTML, damit die Liste klein bleibt. */
export const CustomerMailListItemSchema = z.object({
    mailId: z.string().min(1),
    rfqId: z.string().min(1),
    kind: CustomerMailKindSchema,
    kindLabel: z.string(),
    to: z.string(),
    subject: z.string(),
    status: CustomerMailStatusSchema,
    createdAt: z.iso.datetime(),
    sentAt: z.iso.datetime().nullable(),
    /** Kurze Anfrage-Info (Anlass), falls die Anfrage lesbar war. */
    rfqLabel: z.string().nullable(),
    lastError: z.string().nullable(),
});
export const CustomerMailListResponseSchema = z.object({
    mode: CustomerMailModeSchema,
    items: z.array(CustomerMailListItemSchema),
});
/**
 * Die Vorschau. `previewHtml` trägt eine sichtbar unwirksame Vorschau-URL anstelle des Links,
 * der erst bei der Freigabe entsteht.
 */
export const CustomerMailDetailSchema = CustomerMailListItemSchema.extend({
    previewHtml: z.string(),
    text: z.string().nullable(),
    sentBy: z.string().nullable(),
    discardedAt: z.iso.datetime().nullable(),
    discardedBy: z.string().nullable(),
});
export const CustomerMailActionSchema = z.object({
    action: z.enum(['send', 'discard']),
});
export const UpdateCustomerMailModeSchema = z.object({
    mode: CustomerMailModeSchema,
});
